import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { emitBetslipLoginRequested, emitBetslipDepositToPlaceRequested } from "../../contract/betslip-api";
import { AlertType } from "@ppb/the-wall-common/types";
import type { AlertProps, StyledProps } from "@ppb/the-wall-common/types";
import type { InlinePanelProps, InlinePanelOnAction } from "../InlinePanel/InlinePanel.types";
import type { URN, ExchangeSide } from "../../types";
import { getExchangeRunnerTree } from "../../store/state/exchange-markets/exchange-markets-selectors";
import { createRunnerPotentialBetsSelector } from "../../store/state/exchange-betting/runner-potential-bets-selectors";
import {
  getExchangeEligibleBonusByMarketId,
  getRunnerPotentialBetWithBonus,
} from "../../store/state/exchange-betting-bonus/exchange-betting-bonus-selectors";
import { getExchangePotentialState } from "../../store/state/exchange-betting-potential/exchange-betting-potential-selectors";
import {
  BETTING__EXC_INCREMENT_SIZE_ACTION,
  BETTING__NUDGE_DOWN_POTENTIAL_BET_ACTION,
  BETTING__NUDGE_UP_POTENTIAL_BET_ACTION,
  BETTING__UPDATE_POTENTIAL_BET_ACTION,
  BettingExchangeIncrementSizeAction,
  NudgeDownPotentialBetAction,
  NudgeUpPotentialBetAction,
  UpdatePotentialBetAction,
  BETTING__REMOVE_ALL_POTENTIAL_BETS_ACTION,
  type RemoveAllPotentialBetsAction,
} from "../../store/state/exchange-betting-potential/exchange-betting-potential-actions";
import { getRunnerSPBet } from "../../store/state/exchange-betting/exchange-betting-selectors";
import {
  BetslipExchangePlaceBetClickAction,
  BetslipExchangePriceNudgeDownClickAction,
  BetslipExchangePriceNudgeUpClickAction,
  UI__BETSLIP_EXC_PLACE_BET_CLICK,
  UI__BETSLIP_EXC_PRICE_NUDGE_UP,
  UI__BETSLIP_EXC_PRICE_NUDGE_DOWN,
  BetslipExchangeIncrementSizeAction,
  UI__BETSLIP_EXC_INCREMENT_SIZE_ACTION,
  BetslipExchangeRemovePotentialBetClickAction,
  UI__BETSLIP_EXC_REMOVE_POTENTIAL_BET_CLICK,
  BetslipExchangeBonusChangeAction,
  UI__BETSLIP_EXC_BONUS_CHANGE,
  UI__BETSLIP_CLOSE_CLICK,
  BetslipCloseAction,
  BetslipExchangeLoginToPlaceBetClickAction,
  UI__BETSLIP_EXC_LOGIN_TO_PLACE_BET_CLICK,
  UI__BETSLIP_EXC_PLACE_BET_CLICK_AUTO_CONFIRM,
} from "@ppb/tbd-store/actions/betslip";
import {
  BETTING__EXC_PLACE_BETS,
  BETTING__REMOVE_POTENTIAL_BET_ACTION,
  BETTING__BONUS_TOGGLE_BET_ACTION,
  BETTING__DEPOSIT_TO_PLACE_BET,
  BETTING__UPDATE_SP_ODDS_LIMIT,
  BettingExchangePlaceBetsAction,
  RemovePotentialBetAction,
  BettingBonusToggleBetAction,
  BettingDepositToPlaceBetAction,
  UpdateSpOddsLimitAction,
} from "@ppb/tbd-store/actions/betting";
import { isEachWayMarket, getInlineBetslipTitle, isLineBettingType } from "../../helpers/exchange-betslip-helper";
import { isBspEligible } from "../../helpers/bsp-eligibility";
import { Product } from "@ppb/tbd-store/state/entities";
import {
  getBetslipPlaceStatus,
  getBetslipExchangeContext,
  getBetslipExchangePlaceError,
  getIsFreeBetsSelected,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import {
  buildFreeBetsLabel,
  computeEachWayExposure,
  getBetData,
  getEachWayBetData,
  OutcomeSegmentsData,
} from "../betslip-formatters";
import {
  buildMarketError,
  buildPlaceError,
  buildPriceError,
  buildQuickStakesSelector,
  buildSizeError,
} from "../betslip-mapper";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { i18n } from "../../helpers/i18n";
import { isBPEAvailable } from "../../helpers/best-price-execution";

import type { ExchangeInlinePlacePanelProps } from "./snowflakes/ExchangeInlinePlacePanel/ExchangeInlinePlacePanel.types";
import type {
  BetslipLocalization,
  BetslipSession,
  ExchangeBetslipCapabilities,
  ExchangeBetslipPreferences,
  ExchangeCurrencyDetails,
} from "../../contract";
import {
  BetslipExchangePlaceBetClickAutoConfirm,
  BetslipExchangePriceInputBlurAction,
  BetslipExchangePriceInputChangeAction,
  BetslipExchangeSizeInputChangeAction,
  UI__BETSLIP_EXC_PRICE_INPUT_BLUR,
  UI__BETSLIP_EXC_PRICE_INPUT_CHANGE,
  UI__BETSLIP_EXC_SIZE_INPUT_CHANGE,
} from "../../store/state/betslip-module/betslip-module-exchange-actions";

export type ContainerProps = {
  isEditing?: boolean;
  localization: BetslipLocalization;
  currencyDetails: ExchangeCurrencyDetails;
  session: BetslipSession;
  capabilities: ExchangeBetslipCapabilities;
  preferences: ExchangeBetslipPreferences;
};

type CardProps = {
  prefersConfirm?: boolean;
  runner?: URN;
  isPlacing?: boolean;
  placeError?: {
    notification: AlertProps;
  };
  priceError?: {
    newPrice: number;
    notification: AlertProps;
  };
  sizeError?: {
    newSize: number;
    notification: AlertProps;
  };
  marketError?: {
    notification: AlertProps;
  };
  side?: ExchangeSide;
  quickStakes?: ExchangeInlinePlacePanelProps["quickStakes"];
  price?: ExchangeInlinePlacePanelProps["price"];
  size?: ExchangeInlinePlacePanelProps["size"];
  profitLabel?: ExchangeInlinePlacePanelProps["profitLabel"];
  profitValue?: ExchangeInlinePlacePanelProps["profitValue"];
  profitRawValue?: ExchangeInlinePlacePanelProps["profitRawValue"];
  isSpActive: boolean;
  isOddsLimitActive: boolean;
  oddsLimitTitle: string;
  startingPrice: string;
  oddsLimit?: number;
  bestMarketPrice?: number;
  titlePrefix?: InlinePanelProps["titlePrefix"];
  title: NonNullable<InlinePanelProps["title"]>;
  currencySymbol: ExchangeInlinePlacePanelProps["currencySymbol"];
  betDelay?: ExchangeInlinePlacePanelProps["betDelay"];
  placeBtnLabel: NonNullable<StyledProps["translation"]>;
  pricePlaceholder: NonNullable<ExchangeInlinePlacePanelProps["pricePlaceholder"]>;
  sizePlaceholder: NonNullable<ExchangeInlinePlacePanelProps["sizePlaceholder"]>;
  sizePlaceholderLay: ExchangeInlinePlacePanelProps["sizePlaceholderLay"];
  showMultiplier?: ExchangeInlinePlacePanelProps["showMultiplier"];
  loadingLabel: NonNullable<ExchangeInlinePlacePanelProps["loadingLabel"]>;
  eligibleBonus?: number;
  hasFreeBets?: ExchangeInlinePlacePanelProps["hasFreeBets"];
  isFreeBetsSelected?: ExchangeInlinePlacePanelProps["isFreeBetsSelected"];
  freeBetsLabel?: ExchangeInlinePlacePanelProps["freeBetsLabel"];
  isDepositRequired: boolean;
  isLoggedIn: boolean;
  showMatchMe: boolean;
  matchMePriceAtPlacement?: number;
  outcomeSegments?: OutcomeSegmentsData;
  isMatchMeOn?: boolean;
  isLineBettingType?: boolean;
};

export type StateProps = CardProps | Record<string, never>;

function resolveSpDisplayValues({
  isSpOddsLimit,
  isSpNoLimitBack,
  isSpLayValid,
  side,
  size,
  minProfit,
  betData,
  localization,
}: {
  isSpOddsLimit: boolean;
  isSpNoLimitBack: boolean;
  isSpLayValid: boolean;
  side: ExchangeSide;
  size: number | undefined;
  minProfit: number | undefined;
  betData: { label: string; value: string };
  localization: BetslipLocalization;
}) {
  let pricePlaceholder = i18n({ key: "I18N.BETSLIP.ODDS" });

  if (isSpOddsLimit) {
    pricePlaceholder = i18n({ key: side === "BACK" ? "I18N.BSP.MIN_ODDS" : "I18N.BSP.MAX_ODDS" });
  }

  let profitLabel = betData.label;
  let profitValue = betData.value;

  if (isSpOddsLimit) {
    profitLabel = i18n({ key: "I18N.BSP.MIN_PROFIT" });
    profitValue =
      minProfit !== undefined
        ? currencyFormatWithDecimalPlaces({ ...localization, value: minProfit, decimalPlaces: 2 })
        : betData.value;
  } else if (isSpNoLimitBack) {
    profitLabel = `${i18n({ key: "I18N.BETSLIP.PROFIT" })}:`;
    profitValue = i18n({ key: "I18N.BETSLIP.TBD" });
  } else if (isSpLayValid) {
    profitLabel = `${i18n({ key: "I18N.BETSLIP.LIABILITY" })}:`;
    profitValue = currencyFormatWithDecimalPlaces({ ...localization, value: size ?? 0, decimalPlaces: 2 });
  }

  return { pricePlaceholder, profitLabel, profitValue };
}

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getRunnerPotentialBets = createRunnerPotentialBetsSelector();
  const buildQuickStakes = buildQuickStakesSelector();

  const baseVm = {
    title: "",
    placeBtnLabel: i18n({ key: "I18N.BETSLIP.PLACE_BET" }),
    isDepositRequired: false,
    isLoggedIn: false,
    pricePlaceholder: i18n({ key: "I18N.BETSLIP.ODDS" }),
    sizePlaceholder: i18n({ key: "I18N.BETSLIP.STAKE" }),
    loadingLabel: i18n({ key: "I18N.BETSLIP.PLACING_BET" }),
    oddsLimitTitle: i18n({ key: "I18N.BSP.SP_ODDS_LIMIT_TITLE" }),
    startingPrice: i18n({ key: "I18N.BETSLIP.STARTING_PRICE" }),
    isSpActive: false,
    isOddsLimitActive: false,
    oddsLimit: undefined,
    currencySymbol: "",
  };

  return (
    appState: BetslipApplicationState,
    { localization, currencyDetails, session, capabilities, preferences }: ContainerProps,
  ) => {
    const { currencySymbol } = localization;

    if (!getIsBetslipLoaded(appState)) {
      return {};
    }

    const betslipContext = getBetslipExchangeContext(appState);

    if (!betslipContext) {
      return baseVm;
    }

    const [potentialBet] = getRunnerPotentialBets(appState, betslipContext.runner);
    const runnerTree = getExchangeRunnerTree(appState, betslipContext.runner);

    if (!potentialBet || !runnerTree) {
      return baseVm;
    }

    const { runner, market, marketRunner } = runnerTree;
    const { size, price, side } = potentialBet;
    const { betDelay, status: marketStatus, marketId, marketType, bettingType } = market;
    const showMultiplier = isEachWayMarket(marketType) ? i18n({ key: "I18N.BETSLIP.2X_MULTIPLIER" }) : undefined;
    const { confirmBetPlacement: prefersConfirm, matchMe } = preferences;
    const isMatchMePreferenceEnabled = matchMe.enabled;
    const eligibleBonus = getExchangeEligibleBonusByMarketId(
      appState,
      marketId,
      runner.selectionId,
      marketType,
      bettingType,
    );
    const hasFreeBets = eligibleBonus > 0;
    const quickStakesFormatted = buildQuickStakes(preferences.quickStakes, localization);
    let freeBetsLabel;

    if (hasFreeBets) {
      const translationKey =
        side === "LAY" ? "I18N.BETSLIP.USE_ELIGIBLE_BONUS_LAY" : "I18N.BETSLIP.USE_FREE_BET_BALANCE";

      freeBetsLabel = buildFreeBetsLabel(translationKey, localization, eligibleBonus);
    }

    const sizePlaceholderLay = side === "LAY" ? i18n({ key: "I18N.LABELS.BACKER_STAKE" }) : undefined;

    const isFreeBetsSelected = getIsFreeBetsSelected(appState);
    const exchangePlaceError = getBetslipExchangePlaceError(appState);
    const placeStatus = getBetslipPlaceStatus(appState);
    const isDepositRequired = exchangePlaceError?.errorCode === "INSUFFICIENT_FUNDS";
    const potentialBetData = isFreeBetsSelected
      ? getRunnerPotentialBetWithBonus(appState, runner, marketId, marketType, bettingType)
      : potentialBet;
    const potentialState = getExchangePotentialState(appState, runner.urn, side);

    const isEachWayExposureDisplayOn = capabilities.eachWayExposureDisplay.enabled;
    const eachWayExposure = computeEachWayExposure(
      {
        side,
        selectionId: runner.selectionId,
        price: potentialBetData?.price ?? price,
        size: potentialBetData?.size ?? size,
      },
      { marketType, bettingType, eachWayDivisor: market.eachWayDivisor },
    );
    const betData = getBetData(
      { ...potentialBetData, side },
      localization,
      isDepositRequired,
      isEachWayExposureDisplayOn ? eachWayExposure : undefined,
    );
    const outcomeSegments = getEachWayBetData(eachWayExposure, localization);

    const { isSp, isOddsLimitActive, oddsLimit } = potentialState ?? {};
    const isSpActive =
      isBspEligible(capabilities.bspBetting, market) && !!isSp && (price === undefined || (isOddsLimitActive ?? false));

    const runnerSPBet = getRunnerSPBet(appState, market.urn, marketRunner, side);
    const minProfit = runnerSPBet?.minProfit;
    const minBspLiability = currencyDetails.minBspLiability ?? 10;

    const { loggedIn: isLoggedIn, isAuthenticating } = session;
    const hasCTALoading = !isDepositRequired && isLoggedIn;
    const loadingLabel = hasCTALoading ? baseVm.loadingLabel : "";

    let placeBtnLabel = isDepositRequired
      ? i18n({ key: "I18N.BETSLIP.DEPOSIT_TO_PLACE_BET" })
      : i18n({ key: "I18N.BETSLIP.PLACE_BET" });

    if (!isLoggedIn) {
      placeBtnLabel = i18n({ key: "I18N.BETSLIP.LOGIN_PLACE_BET" });
    }

    const showMatchMe = isBPEAvailable(appState, session, capabilities, preferences);
    const isMatchMeOn = showMatchMe && !!isMatchMePreferenceEnabled;
    const isSpLay = isSpActive && side === "LAY";
    const spLaySizeError =
      isSpLay && size !== undefined && size < minBspLiability
        ? {
            newSize: minBspLiability,
            notification: {
              type: AlertType.Warning,
              message: i18n({
                key: "I18N.BSP.MIN_LIABILITY_ALERT",
                interpolationValues: { amount: `${currencySymbol}${minBspLiability.toFixed(2)}` },
              }),
            } as AlertProps,
          }
        : undefined;

    const isSpOddsLimit = isSpActive && (isOddsLimitActive ?? false);
    const isSpNoLimit = isSpActive && !isOddsLimitActive;
    const isSpNoLimitBack = isSpNoLimit && size !== undefined && side === "BACK";
    const isSpLayValid = isSpNoLimit && size !== undefined && !spLaySizeError && isSpLay;

    const { pricePlaceholder, profitLabel, profitValue } = resolveSpDisplayValues({
      isSpOddsLimit,
      isSpNoLimitBack,
      isSpLayValid,
      side,
      size,
      minProfit,
      betData,
      localization,
    });

    return {
      ...baseVm,
      sizePlaceholderLay,
      titlePrefix:
        side === "BACK" ? i18n({ key: "I18N.BETSLIP.BACK_BET_FOR" }) : i18n({ key: "I18N.BETSLIP.LAY_BET_AGAINST" }),
      title: getInlineBetslipTitle(appState),
      side,
      runner: runner.urn,
      prefersConfirm,
      isPlacing: placeStatus === "INPROGRESS" || !!isAuthenticating,
      quickStakes: quickStakesFormatted,
      size,
      currencySymbol,
      isSpActive,
      isOddsLimitActive: isSpActive ? isOddsLimitActive ?? false : false,
      oddsLimit: isSpOddsLimit ? oddsLimit : undefined,
      bestMarketPrice: side === "BACK" ? runner.back?.[0]?.price : runner.lay?.[0]?.price,
      price: isSpNoLimit ? undefined : price,
      pricePlaceholder,
      sizePlaceholder: isSpLay ? i18n({ key: "I18N.BETSLIP.LIABILITY" }) : i18n({ key: "I18N.BETSLIP.STAKE" }),
      profitLabel,
      profitValue,
      profitRawValue: betData.rawValue,
      sizeError:
        spLaySizeError ?? buildSizeError(marketStatus, localization, currencyDetails, potentialState?.sizeValidation),
      marketError: buildMarketError(marketStatus),
      placeError: buildPlaceError(marketStatus, exchangePlaceError, currencySymbol),
      priceError: buildPriceError(marketStatus, potentialState?.priceValidation, potentialState?.price),
      betDelay,
      hasFreeBets,
      eligibleBonus,
      freeBetsLabel,
      isFreeBetsSelected,
      placeBtnLabel,
      isDepositRequired,
      isLoggedIn,
      loadingLabel,
      showMultiplier,
      showMatchMe,
      matchMePriceAtPlacement: isMatchMeOn ? potentialState?.priceAtPlacement : undefined,
      outcomeSegments,
      isMatchMeOn,
      isLineBettingType: isLineBettingType(bettingType),
    };
  };
};

export type DispatchActions =
  | UpdateSpOddsLimitAction
  | BetslipExchangePlaceBetClickAction
  | BetslipExchangePlaceBetClickAutoConfirm
  | BettingExchangePlaceBetsAction
  | BetslipExchangePriceInputChangeAction
  | BetslipExchangeSizeInputChangeAction
  | BetslipExchangePriceInputBlurAction
  | UpdatePotentialBetAction
  | BetslipExchangePriceNudgeUpClickAction
  | BetslipExchangePriceNudgeDownClickAction
  | NudgeUpPotentialBetAction
  | NudgeDownPotentialBetAction
  | BetslipExchangeIncrementSizeAction
  | BettingExchangeIncrementSizeAction
  | RemovePotentialBetAction
  | BetslipExchangeRemovePotentialBetClickAction
  | BetslipExchangeBonusChangeAction
  | BettingBonusToggleBetAction
  | BetslipCloseAction
  | RemoveAllPotentialBetsAction
  | BettingDepositToPlaceBetAction
  | BetslipExchangeLoginToPlaceBetClickAction;

export type DispatchProps = {
  dispatchExchangePlaceBetAction: (runner: URN, confirmFirst: boolean, priceAtPlacement?: number) => void;
  dispatchOddsLimitToggle: (
    runnerURN: URN,
    side: ExchangeSide,
    isActive: boolean,
    bestPrice?: number,
    currentSize?: number,
  ) => void;
  dispatchExchangePriceInputChangeAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangeSizeInputChangeAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangePriceInputBlurAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangeSizeInputBlurAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangePriceNudgeUpAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangePriceNudgeDownAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangeSizeNudgeUpAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchExchangeSizeNudgeDownAction: (runner: URN, side: ExchangeSide, price?: number, size?: number) => void;
  dispatchIncrementByQuickStakeAction: (
    runner: URN,
    side: ExchangeSide,
    increment: number,
    currencySymbol?: string,
  ) => void;
  dispatchExchangeSelectionRemoveAction: (runner: URN, side: ExchangeSide) => void;
  dispatchExchangeBonusChangeAction: (isFreeBetsSelected: boolean, runner: URN, eligibleBonus: number) => void;
  dispatchCloseAction: InlinePanelOnAction;
  dispatchDepositRedirect: () => void;
  requestLogin: () => void;
  dispatchLoginToPlaceBetAction: (side: ExchangeSide) => void;
};

const dispatchBetslipExchangePlaceBetAction = (
  dispatch: Dispatch<
    BetslipExchangePlaceBetClickAction | BettingExchangePlaceBetsAction | BetslipExchangePlaceBetClickAutoConfirm
  >,
  runner: URN,
  confirmFirst: boolean,
  priceAtPlacement?: number,
): void => {
  dispatch<BetslipExchangePlaceBetClickAction>({
    type: UI__BETSLIP_EXC_PLACE_BET_CLICK,
    payload: { runner, confirmFirst, priceAtPlacement },
  });

  if (!confirmFirst) {
    dispatch<BetslipExchangePlaceBetClickAutoConfirm>({
      type: UI__BETSLIP_EXC_PLACE_BET_CLICK_AUTO_CONFIRM,
      payload: { runner },
    });
  }

  dispatch<BettingExchangePlaceBetsAction>({
    type: BETTING__EXC_PLACE_BETS,
    payload: { runner, confirmFirst, priceAtPlacement },
  });
};

const dispatchBetslipExchangePriceInputChangeAction = (
  dispatch: Dispatch<BetslipExchangePriceInputChangeAction>,
  runner: URN,
  side: ExchangeSide,
  price?: number,
  size?: number,
): void => {
  dispatch<BetslipExchangePriceInputChangeAction>({
    type: UI__BETSLIP_EXC_PRICE_INPUT_CHANGE,
    payload: {
      runner,
      side,
      price,
      size,
    },
  });
};

const dispatchBetslipExchangeSizeInputChangeAction = (
  dispatch: Dispatch<BetslipExchangeSizeInputChangeAction>,
  runner: URN,
  side: ExchangeSide,
  price?: number,
  size?: number,
): void => {
  dispatch<BetslipExchangeSizeInputChangeAction>({
    type: UI__BETSLIP_EXC_SIZE_INPUT_CHANGE,
    payload: {
      runner,
      side,
      price,
      size,
    },
  });
};

const dispatchBetslipExchangePriceInputBlurAction = (
  dispatch: Dispatch<BetslipExchangePriceInputBlurAction>,
  runner: URN,
  side: ExchangeSide,
  price?: number,
  size?: number,
): void => {
  dispatch<BetslipExchangePriceInputBlurAction>({
    type: UI__BETSLIP_EXC_PRICE_INPUT_BLUR,
    payload: {
      runner,
      side,
      price,
      size,
    },
  });
};

const dispatchBettingUpdatePotentialBetAction = (
  dispatch: Dispatch<UpdatePotentialBetAction>,
  runner: URN,
  side: ExchangeSide,
  price?: number,
  size?: number,
): void => {
  dispatch<UpdatePotentialBetAction>({
    type: BETTING__UPDATE_POTENTIAL_BET_ACTION,
    payload: {
      runner,
      side,
      price,
      size,
    },
  });
};

const dispatchBetslipExchangePriceNudgeUpAction = (
  dispatch: Dispatch<BetslipExchangePriceNudgeUpClickAction>,
  runner: URN,
  side: ExchangeSide,
): void => {
  dispatch<BetslipExchangePriceNudgeUpClickAction>({
    type: UI__BETSLIP_EXC_PRICE_NUDGE_UP,
    payload: {
      runner,
      side,
    },
  });
};

const dispatchBettingNudgeUpPotentialBetAction = (
  dispatch: Dispatch<NudgeUpPotentialBetAction>,
  runner: URN,
  side: ExchangeSide,
  price: number | undefined,
  size: number | undefined,
  input: "price" | "size",
): void => {
  dispatch<NudgeUpPotentialBetAction>({
    type: BETTING__NUDGE_UP_POTENTIAL_BET_ACTION,
    payload: {
      runner,
      side,
      price,
      size,
      input,
    },
  });
};

const dispatchBetslipExchangePriceNudgeDownAction = (
  dispatch: Dispatch<BetslipExchangePriceNudgeDownClickAction>,
  runner: URN,
  side: ExchangeSide,
): void => {
  dispatch<BetslipExchangePriceNudgeDownClickAction>({
    type: UI__BETSLIP_EXC_PRICE_NUDGE_DOWN,
    payload: {
      runner,
      side,
    },
  });
};

const dispatchBettingNudgeDownPotentialBetAction = (
  dispatch: Dispatch<NudgeDownPotentialBetAction>,
  runner: URN,
  side: ExchangeSide,
  price: number | undefined,
  size: number | undefined,
  input: "price" | "size",
): void => {
  dispatch<NudgeDownPotentialBetAction>({
    type: BETTING__NUDGE_DOWN_POTENTIAL_BET_ACTION,
    payload: {
      runner,
      side,
      price,
      size,
      input,
    },
  });
};

const dispatchBettingExchangeIncrementSizeAction = (
  dispatch: Dispatch<BettingExchangeIncrementSizeAction>,
  runner: URN,
  side: ExchangeSide,
  increment: number,
): void => {
  dispatch<BettingExchangeIncrementSizeAction>({
    type: BETTING__EXC_INCREMENT_SIZE_ACTION,
    payload: {
      runner,
      side,
      increment,
    },
  });
};

const dispatchBetslipExchangeIncrementSizeAction = (
  dispatch: Dispatch<BetslipExchangeIncrementSizeAction>,
  increment: number,
  currencySymbol?: string,
): void => {
  dispatch<BetslipExchangeIncrementSizeAction>({
    type: UI__BETSLIP_EXC_INCREMENT_SIZE_ACTION,
    payload: {
      increment,
      currencySymbol,
    },
  });
};

const dispatchBettingExchangeRemovePotentialBetAction = (
  dispatch: Dispatch<RemovePotentialBetAction>,
  runner: URN,
  side: ExchangeSide,
): void => {
  dispatch<RemovePotentialBetAction>({
    type: BETTING__REMOVE_POTENTIAL_BET_ACTION,
    payload: {
      runner,
      side,
    },
  });
};

const dispatchBetslipExchangeRemovePotentialBetAction = (
  dispatch: Dispatch<BetslipExchangeRemovePotentialBetClickAction>,
  runner: URN,
): void => {
  dispatch<BetslipExchangeRemovePotentialBetClickAction>({
    type: UI__BETSLIP_EXC_REMOVE_POTENTIAL_BET_CLICK,
    payload: {
      runner,
    },
  });
};

const dispatchBetslipExchangeBonusChangeAction = (
  dispatch: Dispatch<BetslipExchangeBonusChangeAction>,
  isFreeBetsSelected: boolean,
): void => {
  dispatch<BetslipExchangeBonusChangeAction>({
    type: UI__BETSLIP_EXC_BONUS_CHANGE,
    payload: {
      isFreeBetsSelected: !isFreeBetsSelected,
      product: Product.Exchange,
    },
  });
};

const dispatchBettingExchangeBonusUpdateAction = (
  dispatch: Dispatch<BettingBonusToggleBetAction>,
  isFreeBetsSelected: boolean,
  runner: URN,
  marketEligibleBonus: number,
): void => {
  dispatch<BettingBonusToggleBetAction>({
    type: BETTING__BONUS_TOGGLE_BET_ACTION,
    payload: {
      isFreeBetsSelected: !isFreeBetsSelected,
      runner,
      marketEligibleBonus,
    },
  });
};

const dispatchRemoveAllPotentialBetsAction = (dispatch: Dispatch<RemoveAllPotentialBetsAction>): void => {
  dispatch<RemoveAllPotentialBetsAction>({ type: BETTING__REMOVE_ALL_POTENTIAL_BETS_ACTION });
};

const dispatchBetslipCloseAction = (dispatch: Dispatch<BetslipCloseAction>): void => {
  dispatch<BetslipCloseAction>({ type: UI__BETSLIP_CLOSE_CLICK });
};

const dispatchDepositRedirect = (dispatch: Dispatch<BettingDepositToPlaceBetAction>): void => {
  dispatch<BettingDepositToPlaceBetAction>({ type: BETTING__DEPOSIT_TO_PLACE_BET });
  emitBetslipDepositToPlaceRequested({});
};

const dispatchLoginToPlaceBetAction = (
  dispatch: Dispatch<BetslipExchangeLoginToPlaceBetClickAction>,
  side: ExchangeSide,
): void => {
  dispatch<BetslipExchangeLoginToPlaceBetClickAction>({
    type: UI__BETSLIP_EXC_LOGIN_TO_PLACE_BET_CLICK,
    payload: {
      side,
    },
  });
};

const dispatchUpdateSpOddsLimitAction = (
  dispatch: Dispatch<UpdateSpOddsLimitAction>,
  runnerURN: URN,
  side: ExchangeSide,
  activating: boolean,
  bestPrice?: number,
): void => {
  dispatch<UpdateSpOddsLimitAction>({
    type: BETTING__UPDATE_SP_ODDS_LIMIT,
    payload: { runnerURN, side, isActive: activating, oddsLimit: activating ? bestPrice : undefined },
  });
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<DispatchActions>,
) => ({
  dispatchExchangePlaceBetAction: (runner, confirmFirst, priceAtPlacement) => {
    dispatchBetslipExchangePlaceBetAction(dispatch, runner, confirmFirst, priceAtPlacement);
  },
  dispatchOddsLimitToggle: (runnerURN, side, currentValue, bestPrice, currentSize) => {
    const activating = !currentValue;
    dispatchUpdateSpOddsLimitAction(dispatch, runnerURN, side, activating, bestPrice);
    if (activating && bestPrice) {
      dispatchBettingUpdatePotentialBetAction(dispatch, runnerURN, side, bestPrice, currentSize);
    }
  },
  dispatchExchangePriceInputChangeAction: (runner, side, newPrice, size) => {
    dispatchBetslipExchangePriceInputChangeAction(dispatch, runner, side, newPrice, size);
    dispatchBettingUpdatePotentialBetAction(dispatch, runner, side, newPrice, size);
  },
  dispatchExchangeSizeInputChangeAction: (runner, side, price, newSize) => {
    dispatchBetslipExchangeSizeInputChangeAction(dispatch, runner, side, price, newSize);
    dispatchBettingUpdatePotentialBetAction(dispatch, runner, side, price, newSize);
  },
  dispatchExchangePriceInputBlurAction: (runner, side, newPrice, size) => {
    dispatchBetslipExchangePriceInputBlurAction(dispatch, runner, side, newPrice, size);
    dispatchBettingUpdatePotentialBetAction(dispatch, runner, side, newPrice, size);
  },
  dispatchExchangeSizeInputBlurAction: (runner, side, price, newSize) => {
    dispatchBettingUpdatePotentialBetAction(dispatch, runner, side, price, newSize);
  },
  dispatchExchangePriceNudgeUpAction: (runner, side, price, size) => {
    dispatchBetslipExchangePriceNudgeUpAction(dispatch, runner, side);
    dispatchBettingNudgeUpPotentialBetAction(dispatch, runner, side, price, size, "price");
  },
  dispatchExchangePriceNudgeDownAction: (runner, side, price, size) => {
    dispatchBetslipExchangePriceNudgeDownAction(dispatch, runner, side);
    dispatchBettingNudgeDownPotentialBetAction(dispatch, runner, side, price, size, "price");
  },
  dispatchExchangeSizeNudgeUpAction: (runner, side, price, size) => {
    dispatchBettingNudgeUpPotentialBetAction(dispatch, runner, side, price, size, "size");
  },
  dispatchExchangeSizeNudgeDownAction: (runner, side, price, size) => {
    dispatchBettingNudgeDownPotentialBetAction(dispatch, runner, side, price, size, "size");
  },
  dispatchIncrementByQuickStakeAction: (runner, side, increment, currencySymbol) => {
    dispatchBettingExchangeIncrementSizeAction(dispatch, runner, side, increment);
    dispatchBetslipExchangeIncrementSizeAction(dispatch, increment, currencySymbol);
  },
  dispatchExchangeSelectionRemoveAction: (runner, side) => {
    dispatchBetslipExchangeRemovePotentialBetAction(dispatch, runner);
    dispatchBettingExchangeRemovePotentialBetAction(dispatch, runner, side);
  },
  dispatchExchangeBonusChangeAction: (isFreeBetsSelected, runner, eligibleBonus) => {
    dispatchBetslipExchangeBonusChangeAction(dispatch, isFreeBetsSelected);
    dispatchBettingExchangeBonusUpdateAction(dispatch, isFreeBetsSelected, runner, eligibleBonus);
  },
  dispatchCloseAction: () => {
    // Clean potential bets from `bet-engine`, thus cleaning outdated PnL values
    dispatchRemoveAllPotentialBetsAction(dispatch);
    dispatchBetslipCloseAction(dispatch);
  },
  dispatchDepositRedirect: () => dispatchDepositRedirect(dispatch),
  requestLogin: () => {
    emitBetslipLoginRequested();
  },
  dispatchLoginToPlaceBetAction: (side) => dispatchLoginToPlaceBetAction(dispatch, side),
});
